/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/templates/QaTemplateFooter.tsx
 * FILE_ID: QA-TEMPLATES-FOOTER-001
 * PURPOSE: Insert-into summary, live counts, and the insert action.
 */
import { useRef, useState } from 'react'
import { useCloseOnOutsidePointer } from '@quotech/ui'
import { QaTemplateTargetPicker } from './QaTemplateTargetPicker'
import type { QaTemplatePicker } from './useQaTemplatePicker'

export function QaTemplateFooter({ picker }: { picker: QaTemplatePicker }) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(open, () => setOpen(false), [triggerRef, panelRef])
  const label = picker.detail?.location_type_label
    || picker.groups.find((group) => group.location_type === picker.locationType)?.label
    || 'Other'
  const extra = picker.targetAreaIds.size
  const locationSummary = picker.basketLocationLabels.join(', ') || label
  const names = picker.insertNames
  const summary = names.length === 1
    ? names[0]
    : names.length > 1
      ? `${names[0]} + ${names.length - 1}`
      : extra > 0 ? `${locationSummary} + ${extra}` : locationSummary
  const counts = picker.targetAreaIds.size > 1
    ? `${picker.basketCounts.checklists} checklists - ${picker.basketCounts.steps} steps x ${picker.targetAreaIds.size} locations`
    : `${picker.basketCounts.checklists} checklists - ${picker.basketCounts.steps} steps`
  const emptyTemplate = Boolean(picker.detail && picker.detail.sections.length === 0)
  return (
    <div className="relative flex shrink-0 flex-wrap items-center gap-2 border-t border-border px-3 py-2">
      <button
        type="button"
        ref={triggerRef}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex min-h-[32px] items-center rounded-md border border-border px-2 text-xs font-medium"
      >
        Insert into {summary || 'locations'}
      </button>
      <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{counts}</span>
      <button
        type="button"
        data-testid="qa-templates-insert"
        disabled={!picker.canInsert || emptyTemplate}
        onClick={() => void picker.insert()}
        className="inline-flex min-h-[32px] items-center rounded-md bg-foreground px-3 text-xs font-medium text-background disabled:opacity-40"
      >
        {picker.busy ? 'Inserting...' : 'Insert'}
      </button>
      {picker.error ? <p role="alert" className="w-full text-xs text-destructive">{picker.error}</p> : null}
      {open ? (
        <div ref={panelRef} className="absolute bottom-full left-3 right-3 z-10 mb-2 rounded-md border border-border bg-card p-3 shadow-lg">
          <QaTemplateTargetPicker picker={picker} onCollapse={() => setOpen(false)} />
        </div>
      ) : null}
    </div>
  )
}
